import { Vector3 } from 'three';
import { degToRad } from 'three/src/math/MathUtils.js';
import { ArrowElement, Color, Element, LineElement } from '@thive/canvas';
import { InterAreaConnection } from '@thive/fddm-types';
import { ArcElement } from '@thive/canvas/lib/common/types';

import { Z_INDEX_MAP } from '@/modules/canvas/constants/elements';
import { calcBubbleElementScales } from '@/modules/canvas/helpers';
import { theme } from '@/modules/common/theme';
import { FDDM_TAG, INTERACTIVITY_OFF } from '@/modules/floorplanDesign/constants';
import { useContextMenuStore } from '@/modules/contextMenu/useContextMenuStore';
import { ConnectionDirections } from '@/modules/contextMenu/types';

const NON_BUBBLE_LOD_TAGS = new Set<string>([FDDM_TAG.CROSSING, FDDM_TAG.DISTANT_CONNECTION]);

const getBubbleAdditionalTags = (additionalTags: string[]): string[] =>
  additionalTags.filter((tag) => !NON_BUBBLE_LOD_TAGS.has(tag));

const getArcFill = (checked: boolean, selected: boolean): Color => {
  if (!checked) return Color.fromHex(theme.palette.neutral.lighter);
  if (selected) return Color.fromHex(theme.palette.primary.main);
  return Color.fromHex(theme.palette.primary.light);
};

const createArc = (
  id: string,
  parentId: string,
  radius: number,
  checked: boolean,
  selected: boolean,
  areaIdA: string,
  areaIdB: string,
  index: number,
  additionalTags: string[] = [],
  visible = true,
): Element => {
  const thetaStart = degToRad(90 + 180 * index);
  const values: ConnectionDirections[] = ['aToBConnections', 'bToAConnections'];

  const arc: ArcElement = {
    type: 'Arc',
    name: `${id}-curve-${values[index]}`,
    id: `${id}-curve-${values[index]}`,
    center: new Vector3(0, 0, Z_INDEX_MAP[FDDM_TAG.BUBBLE]),
    radius,
    thetaStart,
    thetaLength: Math.PI,
    // when selected connection changes (i.e. usePivots gets updated) it creates a new object, creating a new ArcElement, but it should retain the color...
    fill: getArcFill(checked, selected),
    interactivity: {
      clickable: true,
      hoverable: false,
      selectable: false,
      draggable: false,
      scalable: false,
      rotatable: false,
    },
    parentId,
    tags: [
      FDDM_TAG.INTER_AREA_CONNECTION,
      index === 0 ? FDDM_TAG.A_TO_B_CONNECTION : FDDM_TAG.B_TO_A_CONNECTION,
      areaIdA,
      areaIdB,
      FDDM_TAG.BUBBLE,
      FDDM_TAG.LAYER_CONNECTIONS,
      FDDM_TAG.LAYER_CONNECTION_BUBBLES,
      ...additionalTags,
    ],
    visible,
  };

  return arc;
};

export const createBubbleElement = (
  id: string,
  parentId: string,
  zoom: number,
  interAreaConnection: InterAreaConnection,
  additionalTags: string[] = [],
  visible = true,
): Element[] => {
  const elements: Element[] = [];
  const activeConnectionTab = useContextMenuStore.getState().activeConnectionTabState;
  const relatedComponentId = useContextMenuStore.getState().relatedComponentIdState;
  const hasActiveSelection = relatedComponentId === id && !!activeConnectionTab;
  const bubbleAdditionalTags = getBubbleAdditionalTags(additionalTags);

  const { radius, dividerStrokeWidth, arrowStrokeWidth } = calcBubbleElementScales(zoom);

  const divider: LineElement = {
    type: 'Line',
    name: `${id}-divider`,
    id: `${id}-divider`,
    pointA: new Vector3(0, radius * 2, Z_INDEX_MAP[FDDM_TAG.BUBBLE_DIVIDER]),
    pointB: new Vector3(0, -radius * 2, Z_INDEX_MAP[FDDM_TAG.BUBBLE_DIVIDER]),
    strokeWidth: dividerStrokeWidth,
    stroke: Color.fromHex(theme.palette.shades.light),
    interactivity: INTERACTIVITY_OFF,
    tags: [
      FDDM_TAG.BUBBLE_DIVIDER,
      FDDM_TAG.LAYER_CONNECTIONS,
      FDDM_TAG.LAYER_CONNECTION_BUBBLES,
      ...bubbleAdditionalTags,
    ],
    parentId,
    visible,
  };
  elements.push(divider);

  elements.push(
    createArc(
      id,
      parentId,
      radius,
      interAreaConnection.aToBConnections.length > 0,
      hasActiveSelection ? activeConnectionTab === 'aToBConnections' : true,
      interAreaConnection.idA,
      interAreaConnection.idB,
      0,
      bubbleAdditionalTags,
      visible,
    ),
  );
  elements.push(
    createArc(
      id,
      parentId,
      radius,
      interAreaConnection.bToAConnections.length > 0,
      hasActiveSelection ? activeConnectionTab === 'bToAConnections' : true,
      interAreaConnection.idA,
      interAreaConnection.idB,
      1,
      bubbleAdditionalTags,
      visible,
    ),
  );

  for (let i = -1; i < 2; i += 2) {
    const arrow: ArrowElement = {
      type: 'Arrow',
      name: `${id}-Arrow${i}`,
      id: `${id}-Arrow${i}`,
      pointA: new Vector3(radius * i, radius * -i, Z_INDEX_MAP[FDDM_TAG.BUBBLE_ARROW]),
      pointB: new Vector3(radius * i, radius * i, Z_INDEX_MAP[FDDM_TAG.BUBBLE_ARROW]),
      strokeWidth: arrowStrokeWidth,
      stroke: Color.fromHex(theme.palette.shades.light),
      interactivity: INTERACTIVITY_OFF,
      tags: [
        FDDM_TAG.BUBBLE_ARROW,
        i === -1 ? FDDM_TAG.A_TO_B_CONNECTION : FDDM_TAG.B_TO_A_CONNECTION,
        FDDM_TAG.LAYER_CONNECTIONS,
        FDDM_TAG.LAYER_CONNECTION_BUBBLES,
        ...bubbleAdditionalTags,
      ],
      parentId,
      visible,
    };
    elements.push(arrow);
  }

  return elements;
};
